'use client'

import { useInterval } from "usehooks-ts";
import { Dispatch } from "react";
import { WorkflowAction } from "./useWorkflow";
import { AvatarResponse, pollAvatarStatus } from "@/lib/avatarSdkApi";
import { ingestAndSaveAvatar } from "@/lib/avatarActions";

interface UseAvatarStatusPollerOptions {
    playerUid?: string;
    avatarId?: string;
    pollingInterval: number | null;
    currentAvatarStatus?: string;
    dispatch: Dispatch<WorkflowAction>;
}

export function useAvatarStatusPoller({
    playerUid,
    avatarId,
    pollingInterval,
    currentAvatarStatus,
    dispatch
}: UseAvatarStatusPollerOptions): void {
    useInterval(async () => {
        console.log("Polling avatar status", { playerUid, avatarId, currentAvatarStatus });
        if (!playerUid || !avatarId || currentAvatarStatus === 'Completed' || currentAvatarStatus === 'Failed')
            return;

        let result = await pollAvatarStatus(playerUid, avatarId);

        if (!result.success) return;

        dispatch({
            type: 'AVATAR_STATUS_UPDATED', avatar: {
                ...result.data,
                avatarId: result.data.code
            }
        });

        if (result.data.status === 'Completed') {
            await handleAvatarCompleted(result.data);
        }

        if (result.data.status === 'Failed') {
            dispatchAvatarFailed(dispatch, result.data, 'Avatar creation failed');
        }
    }, pollingInterval);

    async function handleAvatarCompleted(avatarResponse: AvatarResponse) {
        if (!playerUid || !avatarId) return;

        console.log('Ingesting avatar:', avatarResponse);
        const ingestResult = await ingestAndSaveAvatar(playerUid, avatarId);

        if (!ingestResult.success) {
            console.error('Failed to ingest avatar:', ingestResult.error);

            dispatchAvatarFailed(dispatch, avatarResponse, ingestResult.error);

            return;
        }

        dispatch({
            type: 'AVATAR_COMPLETED',
            avatar: {
                ...avatarResponse,
                avatarId: avatarResponse.code
            },
            avatarDownloadUrl: ingestResult.success ? ingestResult.avatarDownloadUrl : undefined
        });
    }
}

function dispatchAvatarFailed(dispatch: Dispatch<WorkflowAction>, data: any, errorMessage?: string) {
    dispatch({
        type: 'AVATAR_FAILED',
        avatar: {
            ...data,
            avatarId: data.code
        },
        error: errorMessage ?? ''
    });
}
